import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import round from 'lodash/round';
import endsWith from 'lodash/endsWith';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import { trackClickEvent } from '@/utils/site-track';

export interface HotCitiesType {
  id: string;
  name: string;
  pinyin: string;
  avg: number;
  avg_by_month: Record<string, number>;
}

const CityCard = React.memo(
  (props: { data: HotCitiesType; dataEleGroup?: string }) => {
    const {
      data: { pinyin, name, avg, avg_by_month: avgByMonth },
      dataEleGroup = 'card_城市',
    } = props;
    const chartRef = React.useRef(null);
    const router = useRouter();

    const config = React.useMemo<ECOption>(() => {
      const monthRange = Object.keys(avgByMonth)
        .map((item) => parseInt(item.split('-')[1]))
        .slice(-3);
      const avgRange = Object.values(avgByMonth)
        .slice(-3)
        .map((item) => round(item, -2));

      return {
        grid: {
          show: false,
          tooltip: { show: false },
          top: 20,
          left: 2,
          right: 2,
          bottom: 0,
        },
        xAxis: {
          type: 'category',
          data: monthRange,
          show: false,
          boundaryGap: false,
        },
        yAxis: {
          type: 'value',
          show: false,
        },
        series: [
          {
            type: 'line',
            silent: true,
            data: avgRange,
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            triggerLineEvent: false,
            lineStyle: {
              color: 'rgba(234,88,12,0.5)',
              width: 3,
            },
            itemStyle: { color: 'rgba(234,88,12)' },
            legendHoverLink: false,
          },
        ],
      };
    }, []);

    return (
      <div
        className="bg-cyan-50 cursor-pointer bg-opacity-60 rounded-lg px-5 py-6 flex items-center justify-between group border border-transparent hover:border-web-blue-500 hover:shadow-md"
        data-observer-id={pinyin}
        data-ele-group={dataEleGroup}
        data-ele-id={`${pinyin}_${name}`}
        onClick={(e) => {
          trackClickEvent(e);
          router.push(`/${pinyin}/`);
        }}
      >
        <div className="grow mr-2.5">
          <Link
            href={`/${pinyin}/`}
            onClick={(e) => trackClickEvent(e, true)}
            className="inline-block text-2xl font-semibold mb-3 tablet:group-hover:text-web-blue-500"
          >
            {`${endsWith(name, '市') ? name.slice(0, -1) : name}招聘行业数据`}
          </Link>
          <p className="text-base font-semibold mb-1.5">{`￥${round(
            avg,
            -2,
          )}`}</p>
          <div className="w-full h-12 [&_canvas]:cursor-pointer">
            <CusEcharts chartRef={chartRef} config={config} />
          </div>
        </div>
      </div>
    );
  },
);

export default CityCard;
